import { DFPEmpty } from "@/components/empty/dfp-empty";
import { DFPModal } from "@/components/modals/dfp-modal";
import { DFPTable } from "@/components/tables/dfp-table";
import {
	type SortOrder,
	baseInitialState,
	baseTableReducer,
} from "@/components/tables/shared-table-state";
import type {
	LocationTableAction,
	LocationTableState,
} from "@/models/tables/location-table/location-table.model";
import { useUserStore } from "@/stores";
import {
	addSortOrderToColumns,
	createTableChangeHandler,
} from "@/utils/table-sort-utils";
import {
	getSessionValue,
	setSessionValue,
	SESSION_KEYS,
} from "@/utils/session-storage";
import { DownloadOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Divider, Pagination, Space, Tooltip } from "antd";
import { Modal, message as antdMessage } from "antd";
import { type Key, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DeleteButton } from "../Admin/SubmissionsTable/DeleteButton";
import { DeletedSwitch } from "../Admin/SubmissionsTable/DeletedSwitch";
import { getOrgColumns } from "./LocationsColumns";
import {
	SmartFilter,
	type FilterCriteria,
	LOCATION_FILTERABLE_COLUMNS,
	CUSHMAN_FILTERABLE_COLUMNS,
} from "./children/SmartFilter";
import { DB_ORG } from "@/constants/db";
import { LocationExporter } from "./children/LocationExportDialog";
import { clearLocationCache, fetchData } from "./childFunctions/fetchData";
import { initializeColumns } from "./childFunctions/initializeColumns";
import { AddLocationModal } from "./children/AddLocationModal";
import { ImportLocationsModal } from "./children/ImportLocationsModal";
import { ImportHistoryModal } from "./children/ImportHistoryModal";

// Load initial smart filters from session storage
const getInitialSmartFilters = (): FilterCriteria[] => {
	return getSessionValue<FilterCriteria[]>(
		SESSION_KEYS.LOCATION_SMART_FILTERS,
		[],
	);
};

const initialState: LocationTableState = {
	...baseInitialState,
	sortBy: "address1",
	pageSize: 25,
	showExportModal: false,
	checkedLocations: [],
	addLocationModal: false,
	showDeleteModal: false,
	smartFilters: getInitialSmartFilters(),
	viewingDeleted: false,
};

// Reducer function
function locationTableReducer(
	state: LocationTableState,
	action: LocationTableAction,
): LocationTableState {
	switch (action.type) {
		case "SET_SHOW_EXPORT_MODAL":
			return { ...state, showExportModal: action.value };
		case "SET_CHECKED_LOCATIONS":
			return { ...state, checkedLocations: action.value };
		case "SET_ADD_LOCATION_MODAL":
			return { ...state, addLocationModal: action.value };
		case "SET_SHOW_DELETE_MODAL":
			return { ...state, showDeleteModal: action.value };
		case "SET_SMART_FILTERS":
			return { ...state, smartFilters: action.value };
		case "SET_VIEWING_DELETED":
			return { ...state, viewingDeleted: action.value };
		default:
			return baseTableReducer(state, action);
	}
}

// Submissions Table component
const LocationsTable = () => {
	const { user } = useUserStore();
	const location = useLocation();
	const navigate = useNavigate();
	const organization = user?.organization?.external_firebase_id;

	// Get initial columns based on organization
	const defaultColumns = getOrgColumns(organization || "");
	const getColumnsShown = (columns: any[]) => {
		const shown: Record<string, boolean> = {};
		for (const col of columns) {
			if (col.key) {
				shown[col.key.toString()] = true;
			}
		}
		return shown;
	};

	const [state, dispatch] = useReducer(locationTableReducer, {
		...initialState,
		tableColumns: defaultColumns,
		columnsShown:
			(location.state as any)?.columnsShown || getColumnsShown(defaultColumns),
		pageNum: (location.state as any)?.pageNum || initialState.pageNum,
		pageSize: (location.state as any)?.pageSize || initialState.pageSize,
		searchVal: (location.state as any)?.searchVal || initialState.searchVal,
		sortBy: (location.state as any)?.sortBy || initialState.sortBy,
		sortOrder:
			((location.state as any)?.sortOrder as SortOrder) ||
			initialState.sortOrder,
	});

	const {
		searchVal,
		sortBy,
		sortOrder,
		pageSize,
		pageNum,
		checkedLocations,
		addLocationModal,
		showDeleteModal,
		isLoading,
		smartFilters,
		viewingDeleted,
	} = state;

	// Get the appropriate filterable columns for the organization
	const filterableColumns = useMemo(() => {
		if (organization === DB_ORG.CUSHMANWAKEFIELD) {
			return CUSHMAN_FILTERABLE_COLUMNS;
		}
		return LOCATION_FILTERABLE_COLUMNS;
	}, [organization]);

	// Handle smart filter changes - update state and session storage
	const handleSmartFiltersChange = (newFilters: FilterCriteria[]) => {
		dispatch({ type: "SET_SMART_FILTERS", value: newFilters });
		setSessionValue(SESSION_KEYS.LOCATION_SMART_FILTERS, newFilters);
	};

	const [exportTriggered, setExportTriggered] = useState(false);

	// Ref to prevent duplicate fetches
	const fetchInProgressRef = useRef(false);

	// Dynamic table scroll height
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const [tableScrollY, setTableScrollY] = useState<number>(400);

	useEffect(() => {
		const calculateScrollY = () => {
			if (tableContainerRef.current) {
				const containerTop = tableContainerRef.current.getBoundingClientRect().top;
				// Subtract: toolbar+divider (~52px) + thead (~30px) + sticky pagination bar (~36px) + footer (~44px)
				const offset = 162;
				const available = window.innerHeight - containerTop - offset;
				setTableScrollY(Math.max(available, 200));
			}
		};
		const timer = setTimeout(calculateScrollY, 50);
		window.addEventListener("resize", calculateScrollY);
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", calculateScrollY);
		};
	}, []);

	useEffect(() => {
		initializeColumns(organization || "", dispatch);
	}, [organization]);

	useEffect(() => {
		// Prevent duplicate simultaneous fetches
		if (fetchInProgressRef.current) {
			return;
		}

		const loadData = async () => {
			fetchInProgressRef.current = true;
			try {
				await fetchData(state, dispatch);
			} finally {
				fetchInProgressRef.current = false;
			}
		};

		loadData();
	}, [pageNum, pageSize, sortBy, sortOrder, searchVal, organization, smartFilters, viewingDeleted]);

	const navigateToLocation = (locationId: string) => {
		const tableState = {
			searchVal,
			sortBy,
			sortOrder,
			pageNum,
		};
		navigate(`/locations/${locationId}`, {
			state: tableState,
		});
	};

	// Add sort order indicators to columns for controlled sorting
	// This ensures the correct sort arrow appears on the active column header
	const columnsWithSortOrder = addSortOrderToColumns(
		state.tableColumns,
		sortBy,
		sortOrder,
	);

	const tableProps = {
		dataSource: state.dataSource,
		columns: columnsWithSortOrder,
		loading: state.isLoading,
		pagination: false as const,
		// Use the unified table change handler for consistent sorting behavior
		onChange: createTableChangeHandler({ pageNum, pageSize }, dispatch),
		onRow: (record: any) => ({
			onClick: () => {
				navigateToLocation(record.id.toString());
			},
		}),
		rowKey: "id",
		rowSelection: {
			selectedRowKeys: checkedLocations,
			onChange: (selectedRowKeys: Key[]) => {
				dispatch({ type: "SET_CHECKED_LOCATIONS", value: selectedRowKeys });
			},
		},
		scroll: { x: "max-content", y: tableScrollY },
	};

	const handleExport = () => {
		setExportTriggered(true);
	};

	const handleToggleAddLocationModal = () => {
		dispatch({ type: "SET_ADD_LOCATION_MODAL", value: !addLocationModal });
	};

	const handleLocationAdded = () => {
		// Clear cache and refresh the table data
		clearLocationCache();
		fetchData(state, dispatch);
	};

	const [showImportModal, setShowImportModal] = useState(false);
	const [showImportHistory, setShowImportHistory] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);

	const handleToggleDeleteModal = () => {
		dispatch({ type: "SET_SHOW_DELETE_MODAL", value: !showDeleteModal });
	};

	const handleDeleteLocations = async () => {
		const isUndelete = viewingDeleted;
		const actionLabel = isUndelete ? "undelete" : "deletion";

		if (!checkedLocations || checkedLocations.length === 0) {
			antdMessage.warning(`No locations selected for ${actionLabel}.`);
			return;
		}

		setDeleteLoading(true);
		try {
			const { deleteLocation, undeleteLocation } = await import("@/services/location-service");

			const actionPromises = checkedLocations.map((locationId: any) => {
				const action = isUndelete ? undeleteLocation(locationId) : deleteLocation(locationId);
				return action
					.then((result) => ({
						locationId,
						success: result.success,
						message: result.message,
						error: null,
					}))
					.catch((error) => ({
						locationId,
						success: false,
						message: null,
						error,
					}));
			});

			const results = await Promise.all(actionPromises);
			const errors = results.filter((result) => !result.success);
			const successes = results.filter((result) => result.success);

			if (successes.length) {
				const verb = isUndelete ? "restored" : "deleted";
				antdMessage.success(
					`${successes.length} location(s) ${verb} successfully.`,
				);
			}

			if (errors.length) {
				const verb = isUndelete ? "restoring" : "deleting";
				errors.forEach((error) =>
					console.error(
						`Error ${verb} location ${error.locationId}:`,
						error.error || error.message,
					),
				);

				const firstError = errors[0];
				if (firstError.message) {
					Modal.error({
						title: isUndelete ? "Cannot Restore Location(s)" : "Cannot Delete Location(s)",
						content: firstError.message,
					});
				} else {
					const verb2 = isUndelete ? "restore" : "delete";
					antdMessage.error(`${errors.length} location(s) failed to ${verb2}.`);
				}
			}

			dispatch({ type: "SET_SHOW_DELETE_MODAL", value: false });
			dispatch({ type: "SET_CHECKED_LOCATIONS", value: [] });

			if (successes.length > 0) {
				clearLocationCache();
				await fetchData(state, dispatch);
			}
		} catch (error) {
			const verb = isUndelete ? "restoring" : "deleting";
			console.error(`Error during ${verb} process:`, error);
			antdMessage.error(`An error occurred while ${verb} locations.`);
		} finally {
			setDeleteLoading(false);
		}
	};

	const handleRefresh = () => {
		clearLocationCache();
		fetchData(state, dispatch);
	};

	const handleViewDeletedChange = (checked: boolean) => {
		dispatch({ type: "SET_VIEWING_DELETED", value: checked });
		// Reset to page 1 when toggling
		if (pageNum !== 1) {
			dispatch({ type: "SET_PAGE_NUM", payload: 1 });
		}
		// Clear cache and re-fetch with new filter
		clearLocationCache();
		fetchData({ ...state, viewingDeleted: checked, pageNum: 1 }, dispatch);
	};

	return (
		<div className="locations-page-container" ref={tableContainerRef}>
			<div className="table-header d-flex justify-content-between">
				<SmartFilter
					columns={filterableColumns}
					filters={smartFilters}
					onFiltersChange={handleSmartFiltersChange}
					data={state.dataSource}
					loading={isLoading}
					showIntegratedSearch={true}
					searchValue={searchVal}
					onSearchChange={(value: string) => {
						dispatch({ type: "SET_SEARCH_VAL", payload: value });
					}}
				/>
				<Space>
					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={isLoading}
					>
						Refresh
					</Button>
					<Button
						icon={<UploadOutlined />}
						onClick={() => setShowImportModal(true)}
					>
						Import
					</Button>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={handleToggleAddLocationModal}
					>
						Add Location
					</Button>
					<DeletedSwitch
						isLoading={isLoading}
						viewingDeleted={viewingDeleted}
						onViewDeletedChange={handleViewDeletedChange}
					/>
					<Tooltip title="Export">
						<Button
							icon={<DownloadOutlined style={{ fontSize: "20px" }} />}
							type="text"
							onClick={handleExport}
						/>
					</Tooltip>
					<DeleteButton
						checkedItems={checkedLocations}
						viewingDeleted={viewingDeleted}
						onClick={handleToggleDeleteModal}
					/>
				</Space>
			</div>
			<Divider />
			<DFPTable
				locale={{
					emptyText: state.isLoading ? null : (
						<DFPEmpty
							className="w-100 h-100"
							description="No locations found. Please create a new location, or adjust your search filters."
						/>
					),
				}}
				{...tableProps}
			/>

			{/* Sticky pagination bar */}
			<div className="locations-pagination-bar">
				<span className="pagination-total">
					{state.count > 0
						? `${(pageNum - 1) * pageSize + 1}-${Math.min(pageNum * pageSize, state.count)} of ${state.count} items`
						: "0 items"}
				</span>
				<Pagination
					current={pageNum}
					pageSize={pageSize}
					total={state.count}
					showSizeChanger
					pageSizeOptions={["25", "50", "100"]}
					size="small"
					onChange={(page, newPageSize) => {
						if (newPageSize !== pageSize) {
							dispatch({ type: "SET_PAGE_SIZE", payload: newPageSize });
							dispatch({ type: "SET_PAGE_NUM", payload: 1 });
						} else {
							dispatch({ type: "SET_PAGE_NUM", payload: page });
						}
					}}
				/>
			</div>

			{exportTriggered && (
				<LocationExporter
					triggerExport={exportTriggered}
					searchVal={searchVal}
					onExportComplete={() => setExportTriggered(false)}
				/>
			)}
			<AddLocationModal
				modalOpen={addLocationModal}
				toggle={handleToggleAddLocationModal}
				organization={organization || ""}
				onLocationAdded={handleLocationAdded}
			/>
			<ImportLocationsModal
				open={showImportModal}
				onClose={() => setShowImportModal(false)}
				onImportComplete={handleLocationAdded}
			/>
			<ImportHistoryModal
				open={showImportHistory}
				onClose={() => setShowImportHistory(false)}
			/>
			<DFPModal
				open={showDeleteModal}
				onCancel={() =>
					dispatch({ type: "SET_SHOW_DELETE_MODAL", value: false })
				}
				onOk={handleDeleteLocations}
				confirmLoading={deleteLoading}
			>
				<span>
					Are you sure you want to {viewingDeleted ? "restore" : "delete"} {checkedLocations.length} location(s)?
				</span>
			</DFPModal>
		</div>
	);
};

export { LocationsTable };
